import React, { useState } from 'react';
import api from '../services/api';

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '520px',
    width: '95%',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  title: { fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '0.9rem' },
  input: { width: '100%', padding: '8px', boxSizing: 'border-box' },
  select: { padding: '8px', fontSize: '1rem', minWidth: '200px' },
  button: { padding: '10px 16px', cursor: 'pointer', border: 'none', borderRadius: '4px', marginRight: '8px' },
  primaryBtn: { background: '#28a745', color: '#fff' },
  secondaryBtn: { background: '#6c757d', color: '#fff' },
  footer: { marginTop: '20px', display: 'flex', justifyContent: 'flex-end' },
  helpLink: { fontSize: '0.9rem', color: '#007bff', cursor: 'pointer', textDecoration: 'underline', marginBottom: '12px' },
};

const DisabledWorkerBulkUpdateModal = ({ isOpen, onClose, onSuccess, selectedYear }) => {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !selectedYear) {
      setError('연도와 엑셀 파일을 선택해 주세요.');
      return;
    }
    setError('');
    setResult(null);
    setLoading(true);
    const formData = new FormData();
    formData.append('year', selectedYear);
    formData.append('excelFile', file);
    if (password) formData.append('password', password);
    try {
      const res = await api.post('/disabled-workers/bulk-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setFile(null);
      const el = document.getElementById('disabled-worker-bulk-file');
      if (el) el.value = '';
      setPassword('');
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.message || '일괄 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPassword('');
    setError('');
    setResult(null);
    setShowHelp(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={handleClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>장애인 명단 일괄 등록</h3>

        <span style={modalStyles.helpLink} onClick={() => setShowHelp(true)}>
          📖 엑셀 일괄 등록 설명보기
        </span>

        <form onSubmit={handleSubmit}>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>연도</label>
            <input type="number" style={modalStyles.input} value={selectedYear} readOnly disabled />
          </div>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>엑셀 파일</label>
            <input
              id="disabled-worker-bulk-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={modalStyles.input}
            />
          </div>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>엑셀 비밀번호 (있는 경우만)</label>
            <input
              type="password"
              style={modalStyles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 없는 엑셀은 비워두세요"
            />
          </div>

          {error && <p style={{ color: 'red', fontSize: '0.9rem', marginBottom: '12px' }}>{error}</p>}

          {result && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: '#e8f5e9',
              border: '1px solid #4caf50',
              borderRadius: '6px',
              fontSize: '0.9rem',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>처리 결과</div>
              <div><strong>{result.updatedCount ?? 0}명</strong> 등록/수정됨</div>
              <div><strong>{result.skippedNewCount ?? 0}명</strong> 기존 명단에 없음 (참여자 먼저 등록 필요)</div>
              <div><strong>{result.unchangedCount ?? 0}명</strong> 변경 없음</div>
              {result.errors?.length > 0 && (
                <div style={{ marginTop: '8px', color: '#c62828' }}>
                  <strong>오류:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div style={modalStyles.footer}>
            <button type="button" style={{ ...modalStyles.button, ...modalStyles.secondaryBtn }} onClick={handleClose}>
              닫기
            </button>
            <button type="submit" style={{ ...modalStyles.button, ...modalStyles.primaryBtn }} disabled={loading}>
              {loading ? '처리 중...' : '일괄 등록하기'}
            </button>
          </div>
        </form>
      </div>

      {showHelp && (
        <DisabledWorkerBulkHelpModal onClose={() => setShowHelp(false)} />
      )}
    </div>
  );
};

export default DisabledWorkerBulkUpdateModal;

/** 엑셀 일괄 등록 설명서 팝업 */
const DisabledWorkerBulkHelpModal = ({ onClose }) => {
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  };
  const boxStyle = {
    background: '#fff',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '640px',
    width: '95%',
    maxHeight: '90vh',
    overflowY: 'auto',
  };
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '12px',
    fontSize: '0.9rem',
  };
  const thStyle = { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', textAlign: 'left' };
  const tdStyle = { border: '1px solid #ddd', padding: '8px' };

  const columns = [
    ['성명', '참여자 명단과 매칭용. 반드시 입력', '텍스트'],
    ['주민등록번호', '참여자 명단과 매칭용. 반드시 입력', '텍스트'],
    ['장애인정구분', '1=장애인복지법, 2=국가유공자', '1 또는 2'],
    ['장애유형', '1~16 (지체장애=1, 뇌병변=2, 시각=3, 청각=4, 언어=5, 지적=6, 정신=7, 자폐=8, 신장=9, 심장=10, 호흡기=11, 간=12, 안면=13, 장루요루=14, 뇌전증=15, 국가유공자=16)', '1~16'],
    ['상이등급', '국가유공자 상이등급 (해당 시에만)', '숫자'],
    ['중증(경증) 여부', '1=중증장애인, 2=경증장애인', '1 또는 2'],
    ['장애인정일', 'YYYYMMDD 형식 (예: 20200309)', '8자리 숫자'],
    ['근무직무', '1~9 (관리자=1, 전문가=2, 사무=3, 서비스=4, 판매=5, 농림어업=6, 기능원=7, 장치조립=8, 단순노무=9)', '1~9'],
  ];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: '16px' }}>📋 장애인 명단 엑셀 일괄 등록 가이드</h3>

        <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>1. 엑셀 파일 만들기</h4>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
          <li>새 엑셀 파일을 열고 <strong>첫 번째 행에 아래 컬럼명</strong>을 정확히 입력합니다.</li>
          <li>두 번째 행부터 데이터를 입력합니다.</li>
          <li><strong>모든 값은 숫자로만 입력</strong>합니다. (성명, 주민등록번호 제외)</li>
        </ul>

        <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>2. 컬럼 구성 및 입력 값</h4>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>컬럼명</th>
              <th style={thStyle}>설명</th>
              <th style={thStyle}>입력 형식</th>
            </tr>
          </thead>
          <tbody>
            {columns.map(([col, desc, fmt], i) => (
              <tr key={i}>
                <td style={tdStyle}><strong>{col}</strong></td>
                <td style={tdStyle}>{desc}</td>
                <td style={tdStyle}>{fmt}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>3. 입력 예시</h4>
        <pre style={{
          background: '#f5f5f5',
          padding: '12px',
          borderRadius: '4px',
          fontSize: '0.85rem',
          overflowX: 'auto',
        }}>
{`성명    주민등록번호      장애인정구분  장애유형  상이등급  중증(경증) 여부  장애인정일   근무직무
강춘덕  5502041260516     1            3                 2            20200309   9`}
        </pre>

        <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>4. 주의사항</h4>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
          <li><strong>성명·주민등록번호</strong>로 참여자 명단과 매칭합니다. 먼저 참여자 등록이 되어 있어야 합니다.</li>
          <li>엑셀에 <strong>값이 있는 항목만</strong> 반영되고, 빈 칸은 기존값이 유지됩니다.</li>
          <li>날짜는 <strong>YYYYMMDD</strong> 8자리 숫자로 입력 (예: 20200309).</li>
          <li><strong>입사일·퇴사일</strong>은 참여자 명단의 참여시작일·참여종료일과 자동 연동되며, 이 엑셀에서는 수정할 수 없습니다.</li>
          <li>비밀번호가 있는 엑셀은 비밀번호를 입력한 뒤 업로드하세요.</li>
        </ul>

        <div style={{ marginTop: '24px', textAlign: 'right' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '8px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};
